import { FormattedMessage } from "react-intl";
import type { ReactNode } from "react";

import { CertificateMigrationStatus, NotaryProfileInvalidFields } from "graphql_globals";
import { VerticalStepIndicator } from "common/signer/common";
import Button from "common/core/button";
import IdentityShield from "assets/images/identity-shield.svg";
import { hardNavigateTo } from "util/navigation";
import Svg from "common/core/svg";

import Styles from "./index.module.scss";
import type {
  NotaryProfileWizardIdentityVerification_notaryProfile as NotaryProfile,
  NotaryProfileWizardIdentityVerification as User,
} from "./index_fragment.graphql";

type Props = {
  user: User;
  renderFooter?: (disabled: boolean) => ReactNode;
};

export const IDENTITY_VERIFICATION_ROUTE = "identity-verification";
type IdentityVerificationType =
  | { id: "IdentityVerification"; completed: boolean; route: typeof IDENTITY_VERIFICATION_ROUTE }
  | false;
function hasVerifiedIdentity(notaryProfile: NotaryProfile): boolean {
  return !notaryProfile.capacities.some((capacity) =>
    capacity.validation.invalidFields.some(
      (invalidField) => invalidField === NotaryProfileInvalidFields.VERIFIED_IDENTITY,
    ),
  );
}

export function identityVerificationSection(
  notaryProfile: NotaryProfile,
): IdentityVerificationType {
  if (
    notaryProfile.certificateMigrationStatus === CertificateMigrationStatus.LEGACY_CERTIFICATE ||
    !notaryProfile.ial2OnboardingEasylink
  ) {
    return false;
  }
  return {
    id: "IdentityVerification",
    route: IDENTITY_VERIFICATION_ROUTE,
    completed: hasVerifiedIdentity(notaryProfile),
  };
}

function IdentityVerification({ user, renderFooter }: Props) {
  return (
    <>
      <div className={Styles.container}>
        <Svg src={IdentityShield} alt="" />
        <div className={Styles.title}>
          <FormattedMessage
            id="df3bd8ce-5988-4245-83f9-0b0cd5833c5e"
            defaultMessage="Next, let's verify your identity"
            tagName="h3"
          />
        </div>
        <p className={Styles.subtext}>
          <FormattedMessage
            id="e7612d5a-60a2-4929-a76a-e9fe9741b1cb"
            defaultMessage="This should take 5-10 minutes. After verifying your identity you can securely sign some documents."
          />
        </p>
        <p className={Styles.subtext}>
          <FormattedMessage
            id="f7a007c0-43ca-4c67-9a76-7c6a01431b0c"
            defaultMessage="Once starting this process you cannot save your progress"
          />
        </p>
        <div className={Styles.bold}>
          <FormattedMessage
            id="1cb6c7ef-59c8-460e-b4e5-1a7ba9833226"
            defaultMessage="First, let's verify your identity"
          />
        </div>
        <VerticalStepIndicator
          className={Styles.stepIndicator}
          listItems={[
            {
              item: (
                <FormattedMessage
                  id="40eae7bd-e427-4be1-b64f-ef7007aeb313"
                  defaultMessage="Verify your information"
                />
              ),
              parentheses: (
                <FormattedMessage
                  id="86231177-b14b-47d3-9d14-dbf421fe3b14"
                  defaultMessage="(2 min)"
                />
              ),
            },
            {
              item: (
                <FormattedMessage
                  id="b45c66d3-655d-434f-b5ba-952d8f72b58e"
                  defaultMessage="Verify your identity"
                />
              ),
              parentheses: (
                <FormattedMessage
                  id="b1d488b1-becc-449f-892e-761ba7e4b418"
                  defaultMessage="(5 mins)"
                />
              ),
              description: (
                <FormattedMessage
                  id="874babcf-f185-4be8-b687-95e08e0021c4"
                  defaultMessage="Upload your ID and take a selfie"
                />
              ),
            },
            {
              item: (
                <FormattedMessage
                  id="67475482-b0eb-47da-9e7e-67af243daaf4"
                  defaultMessage="Sign Documents"
                />
              ),
              parentheses: (
                <FormattedMessage
                  id="b1d488b1-becc-449f-892e-761ba7e4b418"
                  defaultMessage="(10 mins)"
                />
              ),
            },
          ]}
        />

        <Button
          onClick={() => hardNavigateTo(user.notaryProfile?.ial2OnboardingEasylink!)}
          buttonSize="large"
          buttonColor="action"
          variant="primary"
          className={Styles.button}
        >
          <FormattedMessage
            id="a0914580-936d-4373-a70a-52cb13d08c20"
            defaultMessage="Begin Identification"
          />
        </Button>
      </div>
      {renderFooter?.(!hasVerifiedIdentity(user.notaryProfile!))}
    </>
  );
}

export default IdentityVerification;
